// Use relative path for production, falls back to development server if needed
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface UploadedFile {
  success: boolean;
  filePath: string;
  fileName: string;
  originalName: string;
  size: number;
  mimeType: string;
}

export const uploadFilesToPublic = async (files: File[]): Promise<UploadedFile[]> => {
  // Validate number of files
  if (!files || files.length === 0) {
    throw new Error('No files selected');
  }
  
  if (files.length > 20) {
    throw new Error('You can upload a maximum of 20 files at once');
  }

  const formData = new FormData();
  
  // Append all files to form data
  files.forEach((file) => {
    formData.append('files[]', file);
  });

  try {
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include', // Include cookies for session handling
      // Don't set Content-Type header - let the browser set it with the boundary
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed with status:', response.status, 'Response:', errorText);
      throw new Error('Upload failed. Please try again.');
    }

    const result = await response.json();
    console.log('Upload response:', result);

    // Handle the response from the PHP backend
    if (!result.success) {
      const errorMsg = result.message || 'Upload failed. Please try again.';
      console.error('Upload failed:', errorMsg);
      throw new Error(errorMsg);
    }

    // Transform the response to match the expected format
    if (Array.isArray(result.files)) {
      return result.files.map((file: any) => ({
        success: true,
        filePath: file.filePath || file.url || `/${file.path || ''}`, // Ensure path starts with /
        fileName: file.fileName || file.name || '',
        originalName: file.originalName || file.name || '',
        size: file.size || 0,
        mimeType: file.mimeType || file.type || ''
      }));
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error uploading files:', error);
    throw error;
  }
};

// For backward compatibility
export const uploadFileToPublic = async (file: File): Promise<string> => {
  const [result] = await uploadFilesToPublic([file]);
  return result.filePath;
};

export const getFileUrl = (filePath: string): string => {
  if (!filePath) return '';
  
  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://') || filePath.startsWith('blob:')) {
    return filePath;
  }
  
  // If it's a relative path, ensure it starts with a slash
  const cleanPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  
  // In production, we can use relative paths since the files are served from the same domain
  return cleanPath;
};
